import { useMutation } from '@apollo/client'
import { Modal, message } from 'antd'
import { useRef, useState } from 'react'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyIcon } from '~/components/ui/MyIcon'
import { GPTVisibleState } from '~/graphqls/customType'
import { AI_CREATE_ARTWORK, AI_PRE_CREATE } from '~/graphqls/gqls'
import {
  AiCreateArtworkPattern,
  QueueType,
  aiCreateArtwork,
  aiCreateArtworkVariables,
  aiPreCreate,
  aiPreCreateVariables,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { useValidateAuth } from '~/hooks/useValidateAuth'
import PreCreateDetail from '../PreCreateDetail'
import { graphQLErrorMessage, trackEvent } from '~/utils'
import { useStores } from '~/hooks/useStores'
import { useValidateIntergal } from '~/hooks/useValidateIntergal'
import classNames from 'classnames'
import styles from '../AiForm.module.css'
import { FormParamsProps } from '../AiForm'
import { useAtom } from 'jotai'
import {
  comsumedPointsAtom,
  gptVisibleStateAtom,
  nComsumedPointsAtom,
  patternAtom,
} from './atom'

export const CreateComp: React.FC<{
  reset: () => void
  getParams: () => Promise<FormParamsProps>
}> = ({ reset, getParams }) => {
  const { validateIsLogin } = useValidateAuth()
  const { store } = useStores()
  const { IsNotEnough, intergalModal } = useValidateIntergal()
  const [gptVisibleState] = useAtom(gptVisibleStateAtom)
  const [pattern] = useAtom(patternAtom)
  const [comsumedPoints] = useAtom(comsumedPointsAtom)
  const [nComsumedPoints] = useAtom(nComsumedPointsAtom)

  const [loading, setLoading] = useState(false)

  const [aiCreateArtworkAction] = useMutation<
    aiCreateArtwork,
    aiCreateArtworkVariables
  >(AI_CREATE_ARTWORK)

  const aiPreCreateAction = useImperativeQuery<
    aiPreCreate,
    aiPreCreateVariables
  >(AI_PRE_CREATE)

  const onPreCreate = async (formParams: FormParamsProps) => {
    return await aiPreCreateAction({
      param: {
        prompt: formParams?.prompt,
        maskImageUrl: formParams?.maskImageUrl,
        initImageUrl: formParams?.initImageUrl,
        size: formParams?.size,
        width: formParams?.width,
        length: formParams?.length,
        model: formParams?.model,
        num: formParams?.num,
        superType: formParams?.superType,
        modelCode: formParams?.modelCode,
        pattern: formParams?.pattern,
        queueType: formParams?.queueType || QueueType.NORMAL,
      },
    })
  }

  // 消耗明细 modal
  const detailModalLoading = useRef(false)
  const detailModal = async () => {
    if (detailModalLoading.current) {
      return
    }
    if (!validateIsLogin()) {
      return
    }

    const formParams = await getParams()

    try {
      detailModalLoading.current = true

      const preRes = await onPreCreate(formParams)

      return Modal.info({
        title: '作画明细',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: 'hookModal dark',
        content: (
          <PreCreateDetail
            detail={preRes.data.pictureGraph?.aiArtworkPayStrategyTips!}
          />
        ),
        okText: '知道了',
        onOk: close => {
          close()
        },
      })
    } catch (err) {
      message.destroy()
      message.error(graphQLErrorMessage(err) || '获取明细失败，请刷新')
    } finally {
      detailModalLoading.current = false
    }
  }

  /** 立即生成 */
  const submitLock = useRef(false)
  const handleSubmit = async () => {
    if (submitLock.current) {
      return
    }
    if (!validateIsLogin()) {
      return
    }

    const formParams = await getParams()

    try {
      submitLock.current = true
      setLoading(true)

      const preRes = await onPreCreate(formParams)

      await aiCreateArtworkAction({
        variables: {
          param: {
            chatGptOptimize: formParams?.isGPT,
            pattern: formParams?.pattern,
            modelCode: formParams?.modelCode,
            maskImageUrl: formParams?.maskImageUrl,
            maskZoneImageUrl: formParams?.maskZoneImageUrl,
            styleDecorationOptions: formParams?.styleDecorationOptions,
            character: formParams?.character,
            modelFusion: formParams?.modelFusion,
            prompt: formParams?.prompt,
            size: formParams?.size,
            width: formParams?.width,
            length: formParams?.length,
            superType: formParams?.superType,
            model: formParams?.model,
            styleModel: formParams?.styleModel,
            initImageUrl: formParams?.initImageUrl,
            num: formParams?.num,
            creativityDegree: formParams?.creativityDegree,
            ucPrompt: formParams?.ucPrompt,
            repairTheHand: formParams?.doRepairTheHand,
            initWidth: formParams?.initWidth,
            initHeight: formParams?.initHeight,
            seed: formParams?.seed,
            cfg: formParams?.cfg,
            ensd: formParams?.ensd,
            clipSkip: formParams?.clipSkip,
            pretreatmentMethod: formParams?.pretreatmentMethod,
            samplerIndex: formParams?.samplerIndex,
            queueType: formParams?.queueType || QueueType.NORMAL,
            payStrategy: {
              integralUse: {
                integralUseOnAccelerate:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips.integralUse
                    .integralUseOnAccelerate,
                integralUseOnCreate:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips.integralUse
                    .integralUseOnCreate,
                integralUseOnResolution:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips.integralUse
                    .integralUseOnResolution,
              },
              userRightsUse: {
                dailyFreeCreateTimesUse:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips
                    .userRightsUse.dailyFreeCreateTimesUse,
                deservedCreateTimesUse:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips
                    .userRightsUse.deservedCreateTimesUse,
              },
              vipRightsUse: {
                accelerateTimesUse:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips
                    .vipRightsUse.accelerateTimesUse,
                createTimesUse:
                  preRes.data.pictureGraph?.aiArtworkPayStrategyTips
                    .vipRightsUse.createTimesUse,
              },
            },
          },
        },
      })

      if (!!formParams?.trackEventLabel) {
        try {
          const trackParam = JSON.parse(formParams?.trackEventLabel)
          trackEvent(trackParam)
        } catch (error) {}
      }

      // 重置一些状态
      reset()

      store.ai?.setIsRefreshMemberList(true)
      store.ai?.setIsRefreshTotalInfo(true)
      store.ai?.setIsRefeshPreviewData(true)
      store.ai?.setIsResetSelectProduct(true)

      window.scroll({
        top: pattern === AiCreateArtworkPattern.GENERAL ? 0 : 190,
      })
    } catch (err) {
      /** 积分不足 */
      if (IsNotEnough(err)) {
        intergalModal()
      } else {
        message.destroy()
        message.error(graphQLErrorMessage(err) || '生成失败')
      }
    } finally {
      setLoading(false)
      submitLock.current = false
    }
  }

  return (
    <div className="sticky pb-8 bottom-0 bg-neutral-100">
      <div className="shadow-[0_-3px_3px_0px_rgba(252,252,253,0.08)] w-full h-1 mb-2"></div>
      <div className="flex items-center gap-3">
        {gptVisibleState == GPTVisibleState.idle ? (
          <MyBtn
            className={classNames('flex-1', 'relative')}
            type="primary"
            size="large"
            htmlType="submit"
            onClick={handleSubmit}
            loading={loading}
          >
            {!!comsumedPoints ? (
              <>
                <div className="text-base !leading-none font-semibold mt-0.5">
                  立即生成
                </div>
                <div
                  className="text-xs"
                  style={{
                    transform: 'scale(0.833)',
                  }}
                >
                  {comsumedPoints !== nComsumedPoints ? (
                    <>
                      <span>
                        {'('}消耗{nComsumedPoints}积分
                      </span>
                      <span className={classNames('ml-1', styles.delPrice)}>
                        {comsumedPoints}积分{')'}
                      </span>
                    </>
                  ) : (
                    <>
                      {'('}消耗{comsumedPoints}积分{')'}
                    </>
                  )}
                </div>
              </>
            ) : (
              '立即生成'
            )}

            <div
              className="rounded-full w-8 h-8 absolute right-2 top-1.5 flex items-center justify-center"
              style={{
                background: 'rgba(255, 255, 255, 0.16)',
              }}
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                detailModal()
              }}
            >
              <MyIcon
                icon="file-doc"
                className="block text-[16px] font-normal"
              />
            </div>
          </MyBtn>
        ) : (
          <div
            className="flex-1 items-center justify-center bg-[var(--neutral-200)] !h-[44px] rounded-full"
            onClick={() => {}}
          >
            <div className="flex h-full items-center justify-center">
              <div className="text-[var(--neutral-400)] text-sm font-semibold">
                智能描述 生成中
              </div>
              <img
                alt=""
                src="/images/ai/chatgpt_loading_gray.png"
                className="h-7"
              ></img>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
